import { useState, useEffect, useCallback } from 'react';

export interface GeolocationPosition {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

export interface UseGeolocationReturn {
  position: GeolocationPosition | null;
  error: GeolocationPositionError | null;
  isLoading: boolean;
  isSupported: boolean;
  requestPermission: () => Promise<PermissionState>;
  getCurrentPosition: () => Promise<GeolocationPosition>;
  watchPosition: () => number | null;
  clearWatch: (watchId: number) => void;
}

export function useGeolocation(): UseGeolocationReturn {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<GeolocationPositionError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [watchId, setWatchId] = useState<number | null>(null);

  // Geolocation API 지원 여부 확인
  const isSupported = 'geolocation' in navigator;

  // 권한 상태 확인 및 요청
  const requestPermission = useCallback(async (): Promise<PermissionState> => {
    if (!isSupported) {
      throw new Error('Geolocation이 지원되지 않는 브라우저입니다.');
    }

    try {
      // Permissions API가 지원되는 경우 먼저 현재 상태 확인
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        
        // 이미 허용되었다면 바로 반환
        if (permission.state === 'granted') {
          return 'granted';
        }
        
        // 거부되었다면 바로 반환
        if (permission.state === 'denied') {
          return 'denied';
        }
      }
      
      // 권한 요청을 위해 실제로 getCurrentPosition 호출
      // 이 호출이 브라우저의 권한 요청 팝업을 트리거함
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve('granted'),
          (error) => {
            console.log('Geolocation permission error:', error);
            if (error.code === error.PERMISSION_DENIED) {
              resolve('denied');
            } else {
              // 타임아웃이나 기타 오류는 권한 문제가 아닐 수 있음
              resolve('prompt');
            }
          },
          { 
            timeout: 15000, // 15초로 증가 (사용자가 권한 팝업에 응답할 시간)
            enableHighAccuracy: false, // 빠른 응답을 위해 정확도 낮춤
            maximumAge: 0 // 캐시된 위치 사용하지 않음
          }
        );
      });
    } catch (error) {
      console.error('Permission check failed:', error);
      return 'prompt';
    }
  }, [isSupported]);

  // 현재 위치 한 번 가져오기
  const getCurrentPosition = useCallback((): Promise<GeolocationPosition> => {
    if (!isSupported) {
      return Promise.reject(new Error('Geolocation이 지원되지 않는 브라우저입니다.'));
    }

    setIsLoading(true);
    setError(null);

    return new Promise((resolve, reject) => {
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5분
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const result: GeolocationPosition = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          };
          
          setPosition(result);
          setError(null);
          setIsLoading(false);
          resolve(result);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setError(error);
          setIsLoading(false);
          reject(error);
        },
        options
      );
    });
  }, [isSupported]);

  // 위치 변화 감지 시작
  const watchPosition = useCallback((): number | null => {
    if (!isSupported || watchId !== null) {
      return watchId;
    }

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000
    };

    const id = navigator.geolocation.watchPosition(
      (position) => {
        const result: GeolocationPosition = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };
        
        setPosition(result);
        setError(null);
      },
      (error) => {
        console.error('Watch position error:', error);
        setError(error);
      },
      options
    );

    setWatchId(id);
    return id;
  }, [isSupported, watchId]);

  // 위치 감지 중단
  const clearWatch = useCallback((id: number) => {
    if (isSupported) {
      navigator.geolocation.clearWatch(id);
      if (watchId === id) {
        setWatchId(null);
      }
    }
  }, [isSupported, watchId]);

  // 컴포넌트 언마운트 시 watch 정리
  useEffect(() => {
    return () => {
      if (watchId !== null) {
        clearWatch(watchId);
      }
    };
  }, [watchId, clearWatch]);

  return {
    position,
    error,
    isLoading,
    isSupported,
    requestPermission,
    getCurrentPosition,
    watchPosition,
    clearWatch
  };
}